import {sendEvent} from '@github-ui/hydro-analytics'
import {useQuery, useQueryClient} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

import {FREE_QUOTA_TRIGGER, hasReachedQuotaThreshold, PREMIUM_QUOTA_TRIGGER} from './copilot-chat-helpers'
import {
  type CopilotChatEntitlementQuotas,
  type CopilotChatTrialInfo,
  CopilotLicenseType,
  CopilotPlan,
} from './copilot-chat-types'
import {copilotFeatureFlags} from './copilot-feature-flags'

export type CopilotChatEntitlementResult = {
  licenseType: CopilotLicenseType
  plan?: CopilotPlan
  quotas?: CopilotChatEntitlementQuotas
  trial?: CopilotChatTrialInfo
}

export function useCopilotChatEntitlement(
  initialLicenseType: CopilotLicenseType,
  initialPlan?: CopilotPlan,
  initialQuotas?: CopilotChatEntitlementQuotas,
): [entitlement: CopilotChatEntitlement, reloadQuota: (origin?: string, force?: boolean) => void] {
  const client = useQueryClient()

  const invalidateEntitlement = useCallback(
    () => client.invalidateQueries({queryKey: ['copilot-chat', 'entitlement']}),
    [client],
  )

  const {data} = useQuery<CopilotChatEntitlementResult>({
    queryKey: ['copilot-chat', 'entitlement'],
    queryFn: async () => {
      const response = await verifiedFetchJSON('/github-copilot/chat/entitlement')

      if (!response.ok) {
        throw new Error(`Failed to retrieve Copilot chat entitlement (${response.status} on ${response.url})`)
      }

      return (await response.json()) as CopilotChatEntitlementResult
    },
    enabled:
      initialLicenseType === CopilotLicenseType.LicensedLimited ||
      (initialLicenseType === CopilotLicenseType.LicensedFull && copilotFeatureFlags.premiumRequestQuotasEnabled),
    placeholderData: {
      licenseType: initialLicenseType,
      plan: initialPlan,
      quotas: initialQuotas,
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  const license = data?.licenseType ?? initialLicenseType
  const plan = data?.plan ?? initialPlan
  const quotas = data?.quotas ?? initialQuotas

  const remainingChatPercentage = quotas?.remaining.chatPercentage
  const remainingPremiumPercentage = quotas?.remaining.premiumInteractionsPercentage
  const tbbQuotaToCheck =
    license === CopilotLicenseType.LicensedLimited ? quotas?.chatQuota : quotas?.premiumInteractionsQuota
  const shouldSkipReloadForTBB =
    !!quotas?.tokenBasedBillingEnabled && hasReachedQuotaThreshold(tbbQuotaToCheck, FREE_QUOTA_TRIGGER)
  const shouldSkipReloadForLicensedLimited =
    hasReachedQuotaThreshold({percentRemaining: remainingChatPercentage}, FREE_QUOTA_TRIGGER) || shouldSkipReloadForTBB

  const reloadQuota = useCallback(
    (origin?: string, force?: boolean) => {
      if (
        initialLicenseType === CopilotLicenseType.LicensedLimited ||
        (initialLicenseType === CopilotLicenseType.LicensedFull && copilotFeatureFlags.premiumRequestQuotasEnabled)
      ) {
        if (
          !force &&
          ((copilotFeatureFlags.reduceChatQuotaChecks &&
            license === CopilotLicenseType.LicensedLimited &&
            shouldSkipReloadForLicensedLimited) ||
            (license === CopilotLicenseType.LicensedFull &&
              hasReachedQuotaThreshold({percentRemaining: remainingPremiumPercentage}, PREMIUM_QUOTA_TRIGGER)))
        ) {
          // skip invalidating entitlement if the remaining chat/premium quota is above the trigger threshold
          return
        }
        void invalidateEntitlement()
        sendEvent('dotcom_chat.activate', {target: 'ENTITLEMENTS_RELOAD', origin})
      }
    },
    [
      initialLicenseType,
      invalidateEntitlement,
      license,
      shouldSkipReloadForLicensedLimited,
      remainingPremiumPercentage,
    ],
  )

  return [new CopilotChatEntitlement(license, plan, data?.quotas, data?.trial), reloadQuota]
}

export class CopilotChatEntitlement {
  licenseType: CopilotLicenseType
  plan?: CopilotPlan
  quotas?: CopilotChatEntitlementQuotas
  trial?: CopilotChatTrialInfo

  constructor(
    licenseType: CopilotLicenseType,
    plan?: CopilotPlan,
    quotas?: CopilotChatEntitlementQuotas,
    trial?: CopilotChatTrialInfo,
  ) {
    this.licenseType = licenseType
    this.plan = plan
    this.quotas = quotas
    this.trial = trial
  }

  isLicensed(): boolean {
    return this.licenseType && this.licenseType !== CopilotLicenseType.Unlicensed
  }

  canPurchaseAdditionalQuota(): boolean {
    // whether the plan allows for purchasing additional quota
    // 1. not available for limited (free) users
    // 2. available for pro, pro plus, and student users
    // 3. available for business and enterprise users - but only admins can do that

    // TODO implement checking whether user is an admin for business plan to upgrade to enterprise
    // this is unavailable while we don't really know which of potentially multiple copilot licensed orgs the user is an admin of
    return (
      this.licenseType !== CopilotLicenseType.LicensedLimited &&
      copilotFeatureFlags.premiumRequestQuotasEnabled &&
      (this.plan === CopilotPlan.IndividualPro ||
        this.plan === CopilotPlan.IndividualProPlus ||
        this.plan === CopilotPlan.IndividualStudent)
    )
  }

  canUpgradePlan(): boolean {
    // whether there is a higher plan available for upgrade and the user can make that upgrade
    // 1. limited (free) users don't have an upgrade path, they can only purchase a full plan
    // 2. pro users can upgrade to pro plus
    // 3. student plan users can upgrade to pro
    // 4. business users can upgrade to enterprise - but only admins can do that

    // TODO implement checking whether user is an admin for business plan to upgrade to enterprise
    // this is unavailable while we don't really know which of potentially multiple copilot licensed orgs the user is an admin of
    return (
      this.licenseType === CopilotLicenseType.LicensedLimited ||
      (copilotFeatureFlags.premiumRequestQuotasEnabled &&
        (this.plan === CopilotPlan.IndividualPro ||
          this.plan === CopilotPlan.IndividualStudent ||
          (!!this.quotas?.tokenBasedBillingEnabled && this.plan === CopilotPlan.IndividualProPlus)))
    )
  }

  chatQuotaRemaining(): number {
    return copilotFeatureFlags.premiumRequestQuotasEnabled && this.quotas?.remaining.chatPercentage !== undefined
      ? (this.quotas?.remaining.chatPercentage ?? Infinity)
      : (this.quotas?.remaining.chat ?? Infinity)
  }

  premiumInteractionsQuotaRemaining(): number {
    return this.quotas?.remaining.premiumInteractionsPercentage ?? Infinity
  }

  eligibleForTrial(): boolean {
    return this.trial?.eligible ?? false
  }
}

export function isPaidUser(copilotPlan?: CopilotPlan | null): boolean {
  return Boolean(copilotPlan && copilotPlan !== CopilotPlan.IndividualFree)
}

// Based on self.format_quota_reset_label in packages/copilot/app/public/copilot/quota_data_helper.rb

export function isNotNullOrUndefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined
}
